/**
 * Attachments API endpoints
 */

import { reportApiFailure } from '@/lib/telemetry';
import { normalizeHttpOrigin } from '@/lib/security/csp';
import { buildQueryString, fetchApi } from '../core';
import { objectIdPath } from '../path';
import type {
  AttachmentOwnerType,
  AttachmentQueryParams,
  AttachmentResponse,
  ConfirmUploadData,
  CreateUploadData,
  CreateUploadResponse,
  DownloadUrlResponse,
  PresignedUrlResponse,
  QueryParams,
  TenantStorageUsageResponse,
} from '../types';

export function list(params?: AttachmentQueryParams, componentName: string = 'Unknown') {
  const queryParams: QueryParams | undefined = params
    ? { owner_type: params.owner_type, owner_id: params.owner_id }
    : undefined;

  return fetchApi<AttachmentResponse[]>(
    `/attachments${buildQueryString(queryParams)}`,
    {},
    componentName
  );
}

export function getById(attachmentId: string, componentName: string = 'Unknown') {
  return fetchApi<AttachmentResponse>(
    `/attachments/${attachmentId}`,
    {},
    componentName
  );
}

/**
 * Per-category upload ceilings, mirroring `max_size_for` in
 * `src/services/attachment/validation.rs`.
 *
 * The server is the real gate — this only saves the user a round trip and a
 * generic 400. Documents sit at 25 MB rather than a larger figure because that
 * is clamd's stream limit: anything above it uploads successfully and is then
 * quarantined as unscannable, which is a far worse experience than being told
 * up front. Keep the two in step.
 */
const MAX_UPLOAD_BYTES = {
  theme: 2 * 1024 * 1024,
  certificate: 10 * 1024 * 1024,
  image: 20 * 1024 * 1024,
  document: 25 * 1024 * 1024,
  audio: 200 * 1024 * 1024,
  video: 500 * 1024 * 1024,
} as const;

const DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
]);

const UNTRUSTED_OWNER_TYPES = new Set<AttachmentOwnerType>([
  'Submission',
  'Message',
  'UserProfile',
]);

export function maxUploadBytesFor(
  mimeType: string,
  ownerType?: AttachmentOwnerType
): number | null {
  if (ownerType === 'TenantTheme') return MAX_UPLOAD_BYTES.theme;
  if (ownerType === 'CertificateTemplate') return MAX_UPLOAD_BYTES.certificate;
  if (
    ownerType &&
    UNTRUSTED_OWNER_TYPES.has(ownerType) &&
    (mimeType.startsWith('audio/') || mimeType.startsWith('video/'))
  ) {
    return MAX_UPLOAD_BYTES.document;
  }
  if (mimeType.startsWith('image/')) return MAX_UPLOAD_BYTES.image;
  if (mimeType.startsWith('audio/')) return MAX_UPLOAD_BYTES.audio;
  if (mimeType.startsWith('video/')) return MAX_UPLOAD_BYTES.video;
  if (DOCUMENT_MIME_TYPES.has(mimeType)) return MAX_UPLOAD_BYTES.document;
  // An unrecognised type has no client-side ceiling to apply; the server
  // rejects it on its own allowlist rather than this guard guessing.
  return null;
}

/**
 * Origin of the bucket presigned URLs are expected to point at. It is baked
 * into the page's `connect-src` at build time (lib/security/csp.ts), so a
 * backend presigning against a different bucket is not merely misconfigured —
 * the browser refuses the request outright and reports a bare
 * "Failed to fetch", indistinguishable from the network being down.
 */
export class StorageConfigurationError extends Error {
  constructor(readonly configuredValue: string) {
    super(
      configuredValue
        ? 'NEXT_PUBLIC_STORAGE_ORIGIN must be a valid HTTP(S) URL'
        : 'NEXT_PUBLIC_STORAGE_ORIGIN is required for direct uploads'
    );
    this.name = 'StorageConfigurationError';
  }
}

function configuredStorageOrigin(): string {
  const configuredValue = (process.env.NEXT_PUBLIC_STORAGE_ORIGIN ?? '').trim();
  const origin = normalizeHttpOrigin(configuredValue);
  if (!origin) {
    throw new StorageConfigurationError(configuredValue);
  }
  return origin;
}

export class StorageOriginError extends Error {
  constructor(
    readonly presignedOrigin: string,
    readonly allowedOrigin: string
  ) {
    super(
      `Upload host ${presignedOrigin} is not the configured storage origin ` +
        `${allowedOrigin}; the browser will refuse the upload. The backend's ` +
        `S3_BUCKET and the frontend's NEXT_PUBLIC_STORAGE_ORIGIN disagree.`
    );
    this.name = 'StorageOriginError';
  }
}

export class UploadTooLargeError extends Error {
  constructor(
    readonly sizeBytes: number,
    readonly maxBytes: number
  ) {
    super('upload_too_large');
    this.name = 'UploadTooLargeError';
  }
}

export function createUpload(data: CreateUploadData, componentName: string = 'Unknown') {
  const maxBytes = maxUploadBytesFor(data.mime_type, data.owner_type);
  if (maxBytes !== null && data.size_bytes > maxBytes) {
    return Promise.reject(new UploadTooLargeError(data.size_bytes, maxBytes));
  }

  return fetchApi<CreateUploadResponse>(
    '/attachments/uploads',
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    componentName
  );
}

export async function uploadFile(upload: PresignedUrlResponse, file: File) {
  const headers = new Headers();
  Object.entries(upload.headers).forEach(([key, value]) => {
    if (key.toLowerCase() !== 'content-length') {
      headers.set(key, value);
    }
  });
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', file.type || 'application/octet-stream');
  }

  // Goes straight to the presigned external URL, so it cannot use fetchRawApi
  // (which prefixes the backend base URL and attaches auth). Telemetry uses
  // the fixed label 'presigned-upload' — never the URL itself, whose query
  // string carries credentials.
  const requestId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  const storageOrigin = configuredStorageOrigin();
  const presignedOrigin = normalizeHttpOrigin(upload.url);
  if (!presignedOrigin) {
    throw new Error('Presigned upload URL is not a valid HTTP(S) URL');
  }
  if (presignedOrigin !== storageOrigin) {
    throw new StorageOriginError(presignedOrigin, storageOrigin);
  }

  let response: Response;
  try {
    response = await fetch(upload.url, {
      method: 'PUT',
      headers,
      body: file,
    });
  } catch (error: unknown) {
    reportApiFailure({
      endpoint: 'presigned-upload',
      method: 'PUT',
      componentName: 'attachments-upload',
      requestId,
      message: String(error),
    });
    throw error;
  }

  if (!response.ok) {
    reportApiFailure({
      endpoint: 'presigned-upload',
      method: 'PUT',
      componentName: 'attachments-upload',
      requestId,
      status: response.status,
    });
    throw new Error(response.statusText || 'Upload failed');
  }
}

export function confirmUpload(
  uploadSessionId: string,
  data: ConfirmUploadData = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<AttachmentResponse>(
    `/attachments/uploads/${uploadSessionId}/confirm`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    componentName
  );
}

export function createDownloadUrl(
  attachmentId: string,
  componentName: string = 'Unknown',
  preview: boolean = false,
) {
  return fetchApi<DownloadUrlResponse>(
    `/attachments/${objectIdPath(attachmentId, 'attachment')}/download-url${preview ? '?preview=true' : ''}`,
    { method: 'POST' },
    componentName
  );
}

export function remove(attachmentId: string, componentName: string = 'Unknown') {
  return fetchApi<{ message: string }>(
    `/attachments/${attachmentId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function getStorageUsage(componentName: string = 'Unknown') {
  return fetchApi<TenantStorageUsageResponse>(
    '/attachments/storage/usage',
    {},
    componentName
  );
}
